import { Input, Option, Select } from '@mm/components';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import React, { FunctionComponent, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAutomations } from '../../../../../use-automations';
import { AutomationTemplateContainer } from '../../../AutomationTemplateContainer';
import { generateRandomKeyword } from './randomKeyword';
import {
  automationChannelMessageName,
  automationChannelName,
  COLLECTION_OPTIONS,
  CollectionOption,
  COMMON_SELECT_OPTIONS,
  NUMERIC_OPTIONS,
  QUALIFYING_QUESTION_OPTIONS,
  TRIGGER_OPTIONS_BY_CHANNEL,
} from './TemplateShared';
import styles from './TemplateSharedStyles.module.css';
import { useDisabledInputTriggers } from '../../../../../AutomationEdit/InputTriggers/use-disabled-input-triggers';
import { AutomationTemplateName } from '../../TemplateDefinitions';

interface Props {
  template: AutomationTemplateName;
  maxUnicornSMSQuantity: number;
  maxDonkeySMSQuantity: number;
  maxDonkeyEmailQuantity: number;
  maxUnicornEmailQuantity: number;
  defaultQualifyingQuestionCount?: number;
  enableTriggerDelayMinutes?: number;
  enabledCollectionOptions?: CollectionOption[];

  // Applied to Qualifying Question Select -- s/qualifying question/$value/
  qualifyingQuestionName?: string;

  children: (props: {
    busy: boolean;
    isValid: boolean;
    confirm: () => void;
    channelName?: string;
    channelMessageName?: string;
    triggersSelect: React.ReactNode;
    collectionSelect: React.ReactNode;
    qualifyingQuestionSelect: React.ReactNode;
    unicornSMSSelect: React.ReactNode;
    unicornSMSQuantity: number;
    unicornEmailSelect: React.ReactNode;
    unicornEmailQuantity: number;
    donkeySMSSelect: React.ReactNode;
    donkeySMSQuantity: number;
    donkeyEmailSelect: React.ReactNode;
    donkeyEmailQuantity: number;
    triggerDelayMinutesSelect: React.ReactNode;
  }) => React.ReactElement;
}
export type TemplateBuilder = FunctionComponent<{
  maxFollowUpCounts: {
    maxUnicornSMSQuantity: number;
    maxDonkeySMSQuantity: number;
    maxDonkeyEmailQuantity: number;
    maxUnicornEmailQuantity: number;
  };
}>;
export const TemplateBuilderFactory: FunctionComponent<Props> = ({
  template,
  children,
  maxDonkeySMSQuantity,
  maxUnicornSMSQuantity,
  maxDonkeyEmailQuantity,
  maxUnicornEmailQuantity,
  defaultQualifyingQuestionCount = 2,
  enableTriggerDelayMinutes = 0,
  qualifyingQuestionName,
  enabledCollectionOptions = ['email', 'phone'],
}) => {
  const { closeTemplate, smsEnabled, activeTemplate } =
    AutomationTemplateContainer.useContainer();
  const { channel } = activeTemplate || {};
  const effectiveCollectionOptions = enabledCollectionOptions.map(
    co => COLLECTION_OPTIONS[co],
  );

  const enabledInputTriggers = useSelector(
    (state: StoreState) => state.campaignsOverview.enabledInputTriggers,
  );

  const [smsKeyword, setSmsKeyword] = useState(generateRandomKeyword());

  const disabledInputTriggers = useDisabledInputTriggers({
    onLinkClick: closeTemplate,
  });

  const TRIGGER_OPTIONS_BY_CHANNEL_FILTERED = TRIGGER_OPTIONS_BY_CHANNEL[
    channel || ''
  ]
    .filter(Boolean)
    .filter(option => !enabledInputTriggers.find(input => input === option.id))
    .map(opt => ({
      ...opt,
      disabled: !!disabledInputTriggers.templates[opt.id],
      tooltip: disabledInputTriggers.templates[opt.id],
    }));

  const [selectedTriggers, setSelectedTriggers] = useState(
    [TRIGGER_OPTIONS_BY_CHANNEL_FILTERED.find(opt => !opt.disabled)].filter(
      Boolean,
    ),
  );
  const [selectedCollectionOptions, setSelectedCollectionOptions] = useState<
    Option<CollectionOption>[]
  >(effectiveCollectionOptions);

  let defaultQualifyingQuestionOption =
    QUALIFYING_QUESTION_OPTIONS[defaultQualifyingQuestionCount - 1];
  if (qualifyingQuestionName) {
    defaultQualifyingQuestionOption = {
      ...defaultQualifyingQuestionOption,
      label: (defaultQualifyingQuestionOption.label as string).replace(
        'qualifying question',
        qualifyingQuestionName,
      ),
    };
  }
  const [
    selectedQualifyingQuestionOptions,
    setSelectedQualifyingQuestionOptions,
  ] = useState<typeof QUALIFYING_QUESTION_OPTIONS>([
    defaultQualifyingQuestionOption,
  ]);

  const [unicornSMSQuantity, setUnicornSMSQuantity] = useState([
    NUMERIC_OPTIONS[maxUnicornSMSQuantity],
  ]);
  const [donkeySMSQuantity, setDonkeySMSQuantity] = useState([
    NUMERIC_OPTIONS[maxDonkeySMSQuantity],
  ]);
  const [unicornEmailQuantity, setUnicornEmailQuantity] = useState([
    NUMERIC_OPTIONS[maxUnicornEmailQuantity],
  ]);
  const [donkeyEmailQuantity, setDonkeyEmailQuantity] = useState([
    NUMERIC_OPTIONS[maxDonkeyEmailQuantity],
  ]);
  const [triggerDelayMinutes, setTriggerDelayMinutes] = useState(
    enableTriggerDelayMinutes,
  );

  const { createAutomation } = useAutomations();

  const [busy, setBusy] = useState(false);

  const isValid = Boolean(
    selectedTriggers.length &&
      selectedTriggers.every(opt => !opt.disabled) &&
      selectedCollectionOptions.length &&
      selectedQualifyingQuestionOptions.length,
  );

  const confirm = () => {
    setBusy(true);
    const templateParams = {
      inputTriggers: selectedTriggers.map(option => ({ kind: option.id })),
      collectionOptions: selectedCollectionOptions.map(option => option.id),
      qualifyingQuestions: selectedQualifyingQuestionOptions[0].id,
      unicornSMSQuantity: smsEnabled ? unicornSMSQuantity[0].id : 0,
      donkeySMSQuantity: smsEnabled ? donkeySMSQuantity[0].id : 0,
      unicornEmailQuantity: unicornEmailQuantity[0].id,
      donkeyEmailQuantity: donkeyEmailQuantity[0].id,
    } as any;
    if (triggerDelayMinutes > 0) {
      templateParams.triggerDelayMinutes = triggerDelayMinutes;
    }
    createAutomation(template, templateParams);
    closeTemplate();
  };

  if (!channel) {
    return null;
  }

  return children({
    busy,
    isValid,
    confirm,
    channelName: automationChannelName(channel),
    channelMessageName: automationChannelMessageName(channel),
    collectionSelect: (
      <Select
        {...COMMON_SELECT_OPTIONS}
        disabled={busy}
        options={effectiveCollectionOptions}
        selected={selectedCollectionOptions}
        onChange={setSelectedCollectionOptions}
      />
    ),
    qualifyingQuestionSelect: (
      <Select
        singleSelect
        {...COMMON_SELECT_OPTIONS}
        disabled={busy}
        options={
          qualifyingQuestionName
            ? QUALIFYING_QUESTION_OPTIONS.map(o => ({
                ...o,
                label: (o.label as string).replace(
                  'qualifying question',
                  qualifyingQuestionName,
                ),
              }))
            : QUALIFYING_QUESTION_OPTIONS
        }
        selected={selectedQualifyingQuestionOptions}
        onChange={setSelectedQualifyingQuestionOptions}
      />
    ),
    unicornSMSSelect: (
      <Select
        singleSelect
        {...COMMON_SELECT_OPTIONS}
        disabled={busy}
        options={NUMERIC_OPTIONS.slice(0, maxUnicornSMSQuantity + 1)}
        selected={unicornSMSQuantity}
        onChange={setUnicornSMSQuantity}
      />
    ),
    unicornSMSQuantity: unicornSMSQuantity[0].id,
    unicornEmailSelect: (
      <Select
        singleSelect
        {...COMMON_SELECT_OPTIONS}
        disabled={busy}
        options={NUMERIC_OPTIONS.slice(0, maxUnicornEmailQuantity + 1)}
        selected={unicornEmailQuantity}
        onChange={setUnicornEmailQuantity}
      />
    ),
    unicornEmailQuantity: unicornEmailQuantity[0].id,

    donkeySMSSelect: (
      <Select
        singleSelect
        {...COMMON_SELECT_OPTIONS}
        disabled={busy}
        options={NUMERIC_OPTIONS.slice(0, maxDonkeySMSQuantity + 1)}
        selected={donkeySMSQuantity}
        onChange={setDonkeySMSQuantity}
      />
    ),
    donkeySMSQuantity: donkeySMSQuantity[0].id,
    donkeyEmailSelect: (
      <Select
        singleSelect
        {...COMMON_SELECT_OPTIONS}
        disabled={busy}
        options={NUMERIC_OPTIONS.slice(0, maxDonkeyEmailQuantity + 1)}
        selected={donkeyEmailQuantity}
        onChange={setDonkeyEmailQuantity}
      />
    ),
    donkeyEmailQuantity: donkeyEmailQuantity[0].id,

    triggersSelect: (
      <Select
        {...COMMON_SELECT_OPTIONS}
        disabled={busy}
        options={TRIGGER_OPTIONS_BY_CHANNEL_FILTERED}
        selected={selectedTriggers}
        onChange={setSelectedTriggers}
      />
    ),

    triggerDelayMinutesSelect: (
      <Input
        value={triggerDelayMinutes.toString()}
        css={{
          container: classNames(
            styles.inputContainer,
            styles.numberInputContainer,
          ),
          input: styles.input,
        }}
        type="number"
        onChange={e => setTriggerDelayMinutes(Number(e.target.value))}
      />
    ),
  });
};
